import { useState, useEffect } from 'react'
import axios from '../utils/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Textarea } from '../components/ui/textarea'
import { Check, X, Clock, Package, User, Calendar } from 'lucide-react'

export default function ManageRedemptions() {
  const [redemptions, setRedemptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, pending, approved, completed, rejected
  const [processing, setProcessing] = useState({})
  const [adminNotes, setAdminNotes] = useState({})

  useEffect(() => {
    let cancelled = false

    const fetchRedemptions = async () => {
      try {
        setLoading(true)
        const auth = JSON.parse(localStorage.getItem('auth'))
        const token = auth?.token
        if (!token) {
          window.location.href = '/admin/login'
          return
        }
        const response = await axios.get('/admin/redemptions', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (cancelled) return
        setRedemptions(response.data.redemptions || [])
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching redemptions:', error)
        if (error.response?.status === 401) {
          window.location.href = '/admin/login'
        }
        alert('Failed to fetch redemptions')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchRedemptions()
    return () => { cancelled = true }
  }, [])

  const fetchRedemptionsManual = async () => {
    try {
      setLoading(true)
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      const response = await axios.get('/admin/redemptions', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setRedemptions(response.data.redemptions || [])
    } catch (error) {
      console.error('Error fetching redemptions:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert('Failed to fetch redemptions')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStatus = async (redemptionId, status) => {
    const notes = adminNotes[redemptionId] || ''
    
    if (!notes.trim() && status !== 'approved') {
      alert('Please provide admin notes')
      return
    }

    if (!window.confirm(`Are you sure you want to ${status} this redemption?`)) {
      return
    }

    try {
      setProcessing({ ...processing, [redemptionId]: true })
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.put(
        `/admin/redemptions/${redemptionId}`,
        { status, adminNotes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      alert(`Redemption ${status} successfully!`)
      setAdminNotes({ ...adminNotes, [redemptionId]: '' })
      fetchRedemptionsManual()
    } catch (error) {
      console.error('Error updating redemption:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert(error.response?.data?.error || 'Failed to update redemption')
    } finally {
      setProcessing({ ...processing, [redemptionId]: false })
    }
  }

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: 'secondary', icon: Clock },
      approved: { variant: 'default', icon: Check },
      completed: { variant: 'default', icon: Package },
      rejected: { variant: 'destructive', icon: X }
    }
    const config = variants[status] || variants.pending
    const Icon = config.icon
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const filteredRedemptions = filter === 'all' 
    ? redemptions 
    : redemptions.filter(r => r.status === filter)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Manage Redemptions</h1>
        <p className="text-muted-foreground mt-1">Review and process gift redemption requests</p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b">
        {['all', 'pending', 'approved', 'completed', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 font-medium transition-colors border-b-2 capitalize ${
              filter === status
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {status}
            {status !== 'all' && (
              <span className="ml-2 text-xs">
                ({redemptions.filter(r => r.status === status).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Redemptions List */}
      {loading ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">Loading redemptions...</p>
          </CardContent>
        </Card>
      ) : filteredRedemptions.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">No redemptions found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRedemptions.map((redemption) => {
            const isProcessing = processing[redemption.id]
            return (
              <Card key={redemption.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <CardTitle>{redemption.giftItem?.name || 'Unknown Gift'}</CardTitle>
                        {getStatusBadge(redemption.status)}
                      </div>
                      <CardDescription className="mt-2">
                        Requested by {redemption.user?.username || 'Unknown User'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Redemption Info */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">User:</span>
                        <span className="font-medium">{redemption.user?.username}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Gift:</span>
                        <span className="font-medium">{redemption.giftItem?.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Requested:</span>
                        <span>{new Date(redemption.redeemedAt).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Points Used:</span>
                        <span className="ml-2 font-bold text-primary">
                          {redemption.pointsUsed} pts
                        </span>
                      </div>
                      {redemption.userAddress && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Address:</span>
                          <p className="mt-1 text-xs bg-muted p-2 rounded">
                            {redemption.userAddress}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Admin Notes Section */}
                  {redemption.status === 'pending' ? (
                    <div className="space-y-2 border-t pt-4">
                      <label className="text-sm font-medium">Admin Notes</label>
                      <Textarea
                        placeholder="Add notes for this redemption..."
                        value={adminNotes[redemption.id] || ''}
                        onChange={(e) => setAdminNotes({
                          ...adminNotes,
                          [redemption.id]: e.target.value
                        })}
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus(redemption.id, 'approved')}
                          disabled={isProcessing}
                          className="gap-2"
                        >
                          <Check className="h-4 w-4" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(redemption.id, 'completed')}
                          disabled={isProcessing}
                          className="gap-2"
                        >
                          <Package className="h-4 w-4" />
                          Mark Completed
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleUpdateStatus(redemption.id, 'rejected')}
                          disabled={isProcessing}
                          className="gap-2"
                        >
                          <X className="h-4 w-4" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-t pt-4">
                      {redemption.adminNotes && (
                        <div className="space-y-1">
                          <span className="text-sm font-medium">Admin Notes:</span>
                          <p className="text-sm bg-muted p-2 rounded">
                            {redemption.adminNotes}
                          </p>
                        </div>
                      )}
                      {redemption.status === 'approved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(redemption.id, 'completed')}
                          disabled={isProcessing}
                          className="gap-2 mt-2"
                        >
                          <Package className="h-4 w-4" />
                          Mark as Completed
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Showing {filteredRedemptions.length} of {redemptions.length} redemptions</span>
            <div className="flex gap-4">
              <span>{redemptions.filter(r => r.status === 'pending').length} pending</span>
              <span>{redemptions.filter(r => r.status === 'approved').length} approved</span>
              <span>{redemptions.filter(r => r.status === 'completed').length} completed</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
